export const demoYears = [2026, 2027, 2028] as const;
export type DemoYear = typeof demoYears[number];
export const demoIds = ['F-001', 'F-002', 'F-003'] as const;
export type DemoId = typeof demoIds[number];
export interface DemoState {
  property: number;
  year: DemoYear;
  resolved: DemoId[];
  reopened: DemoId[];
}
export interface DemoObservation {
  id: DemoId;
  title: string;
  x: number;
  y: number;
  priority: boolean;
  resolved: boolean;
  status: string;
  assessment: string;
  next: string;
}
export type DemoLocale = 'sv' | 'en';
export const demoYearHistory: Record<DemoLocale, Record<DemoYear, string>> = {
  sv: {
    2026: '2026: Första dokumentationen i det illustrativa scenariot.',
    2027: '2027: En åtgärd dokumenteras för F-001. Övriga observationer följs upp.',
    2028: '2028: F-002 visar en förändring att granska. Tidigare dokumentation behålls.',
  },
  en: {
    2026: '2026: First documentation in the illustrative scenario.',
    2027: '2027: An action is documented for F-001. Other observations are followed up.',
    2028: '2028: F-002 shows a change to review. Earlier documentation is retained.',
  },
};
export const demoDisclaimer: Record<DemoLocale, string> = {
  sv: 'Illustrativ demo med exempeldata. Fotografiet visar miljön; markeringarna är ingen skadeanalys av byggnaden.',
  en: 'Illustrative demo using sample data. The photograph shows the setting; the markers are not a damage assessment of the building.',
};
export const demoProperties: Record<DemoLocale, string[]> = {
  sv: ['Exempelfastighet 01', 'Exempelfastighet 02', 'Exempelfastighet 03'],
  en: ['Sample property 01', 'Sample property 02', 'Sample property 03'],
};

function readIds(value: string | null): DemoId[] {
  const values = (value || '').split(',');
  return demoIds.filter(id => values.includes(id));
}
export function parseDemoState(search: string): { state: DemoState; invalid: boolean } {
  const params = new URLSearchParams(search);
  const propertyText = params.get('property');
  const yearText = params.get('year');
  const property = propertyText !== null && /^[0-2]$/.test(propertyText) ? Number(propertyText) : 0;
  const year = demoYears.find(item => String(item) === yearText) || 2026;
  const resolved = readIds(params.get('resolved'));
  const reopened = readIds(params.get('reopened')).filter(id => !resolved.includes(id));
  const invalidIds = ['resolved', 'reopened'].some(key => {
    const value = params.get(key);
    return !!value && value.split(',').some(id => !demoIds.some(known => known === id));
  });
  return {
    state: { property, year, resolved, reopened },
    invalid: (propertyText !== null && !/^[0-2]$/.test(propertyText))
      || (yearText !== null && !demoYears.some(item => String(item) === yearText)) || invalidIds,
  };
}
export function demoSearch(state: DemoState): string {
  const params = new URLSearchParams({ property: String(state.property), year: String(state.year) });
  if (state.resolved.length) params.set('resolved', state.resolved.join(','));
  if (state.reopened.length) params.set('reopened', state.reopened.join(','));
  return `?${params.toString()}`;
}
export function demoObservations(state: DemoState, locale: DemoLocale = 'sv'): DemoObservation[] {
  const entriesByLocale: Record<DemoLocale, Array<Omit<DemoObservation, 'resolved' | 'status' | 'priority'>>> = {
    sv: [
      { id: 'F-001', title: 'Förändring i fog', x: 32, y: 55,
        assessment: 'Bildmaterialet är ett första underlag. Orsak och omfattning behöver bedömas på plats.',
        next: 'Låt en byggnadsingenjör undersöka fogen och området runt den.' },
      { id: 'F-002', title: 'Förändring i ytskikt', x: 58, y: 72,
        assessment: 'Observationen behöver jämföras med närbilder innan en åtgärd föreslås.',
        next: 'Komplettera med närbilder och bedöm om en fysisk kontroll behövs.' },
      { id: 'F-003', title: 'Detalj vid fönster', x: 26, y: 78,
        assessment: 'Ingen förändring antas i detta exempel. En likvärdig bild behövs för nästa jämförelse.',
        next: 'Dokumentera samma detalj vid nästa överenskomna inspektion.' },
    ],
    en: [
      { id: 'F-001', title: 'Change in joint', x: 32, y: 55,
        assessment: 'The images provide an initial basis. The cause and extent need to be assessed on site.',
        next: 'Ask a building engineer to inspect the joint and the surrounding area.' },
      { id: 'F-002', title: 'Change in surface finish', x: 58, y: 72,
        assessment: 'The observation should be compared with close-up images before any action is proposed.',
        next: 'Add close-up images and assess whether a physical inspection is needed.' },
      { id: 'F-003', title: 'Detail by window', x: 26, y: 78,
        assessment: 'No change is assumed in this example. A comparable image is needed for the next review.',
        next: 'Document the same detail during the next agreed inspection.' },
    ],
  };
  const entries = entriesByLocale[locale];
  return entries.map((entry, index) => {
    const resolved = state.resolved.includes(entry.id) || (
      !state.reopened.includes(entry.id) && state.year >= 2027 && index === 0
    );
    const changed = state.year === 2028 && index === 1;
    return {
      ...entry, resolved,
      priority: !resolved && state.property === 0 && (index === 0 || changed),
      status: locale === 'sv'
        ? resolved ? 'Åtgärdad i exemplet' : changed ? 'Förändrad i exemplet' : state.year === 2026 ? 'Ny observation' : 'Oförändrad i exemplet'
        : resolved ? 'Resolved in the example' : changed ? 'Changed in the example' : state.year === 2026 ? 'New observation' : 'Unchanged in the example',
    };
  });
}
export function toggleResolved(state: DemoState, id: DemoId): DemoState {
  const wasResolved = demoObservations(state).find(item => item.id === id)?.resolved;
  return {
    ...state,
    resolved: wasResolved ? state.resolved.filter(item => item !== id) : [...state.resolved, id],
    reopened: wasResolved ? [...state.reopened, id] : state.reopened.filter(item => item !== id),
  };
}
